import { useChatMessages, useChatStatus } from '@ai-sdk-tools/store';
import { isEmpty, isNotEmpty } from '@newfront-insurance/core';
import { ThinkingMessage } from '@newfront-insurance/nova-core';
import type { ToolUIPart, UIMessagePart } from 'ai';
import { motion } from 'framer-motion';
import get from 'lodash/get';
import { ArrowDownIcon, Loader2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import type { ComponentType, ReactNode } from 'react';
import { PreviewMessage } from './preview-message';
import { useMessages } from '../../../hooks/use-messages';
import type { MessageReaction, UIMessageWithMetadata } from '../../../lib/types';
import { Greeting } from '../../greeting';
// import { InlineError } from '../../inline-error';
import { useShowProgressDataPart } from '../hooks/use-show-progress-data-part';
import { ProgressDataPart, type DataPartRenderer, type ToolOutputRenderer } from '../lib/default-renderers';
import { buildPriorSourcesByMessageId, type SourcesMap } from '../lib/sources';

// Re-export PreviewMessage for external use
export { PreviewMessage };
export interface PreviewMessageProps {
  message: UIMessageWithMetadata;
  isStreaming: boolean;
  isLast: boolean;
  onReaction: (reaction: MessageReaction | null) => void;
  sourceFallbackMap?: SourcesMap;
  toolRenderers?: Record<string, ToolOutputRenderer>;
  dataPartRenderers?: Record<string, DataPartRenderer>;
  beforeContentRenderer?: (message: UIMessageWithMetadata) => ReactNode;
  beforeContentClassName?: string;
  messageContentClassName?: string;
  enableSourcesRendering?: boolean;
}
interface MessagesProps {
  chatUuid?: string;
  className?: string;
  isLoading?: boolean;
  welcomeMessage?: string;
  /**
   * Object mapping tool names to their renderer functions.
   * Keys are tool names (e.g., 'processFiles', 'image_generation').
   * Values are renderer functions that take the part and messageUuid.
   */
  toolRenderers?: Record<string, ToolOutputRenderer>;
  /**
   * Object mapping data part types to their renderer functions.
   * Keys are data part types (e.g., 'data-coverageGapAnalysis').
   * Values are renderer functions that take the part and message.
   */
  dataPartRenderers?: Record<string, DataPartRenderer>;
  /**
   * Custom message renderer component.
   * If provided, this component will be used instead of the default PreviewMessage.
   * Must accept the same props as PreviewMessage.
   */
  messageRenderer?: ComponentType<PreviewMessageProps>;
  /**
   * Controls whether inline citations and sources are rendered.
   * Passed through to PreviewMessage component.
   */
  enableSourcesRendering?: boolean;
}

/**
 * Messages component that displays chat messages using @ai-sdk-tools/store.
 * No prop drilling needed - messages and status are accessed via context.
 * Handles its own loading state overlay.
 * Reads progressMessage from ChatRoot context.
 * Accepts toolRenderers and dataPartRenderers as props for composability.
 *
 * @example Using a custom message renderer:
 * ```tsx
 * <Messages messageRenderer={MyCustomMessageRenderer} />
 * ```
 */
export function Messages({
  chatUuid,
  className,
  isLoading,
  toolRenderers,
  dataPartRenderers,
  messageRenderer: MessageRenderer = PreviewMessage,
  enableSourcesRendering,
  welcomeMessage
}: MessagesProps) {
  const messages = useChatMessages<UIMessageWithMetadata>();
  const status = useChatStatus();
  const [reactions, setReactions] = useState<Record<string, MessageReaction | null>>({});
  const {
    containerRef: messagesContainerRef,
    endRef: messagesEndRef,
    isAtBottom,
    scrollToBottom,
    onViewportEnter,
    onViewportLeave
  } = useMessages({
    chatUuid,
    status
  });
  const showProgressDataPart = useShowProgressDataPart();
  const showThinkingMessage = getShowThinkingMessage(status, messages);
  const priorSourcesByMessageId = useMemo(() => buildPriorSourcesByMessageId(messages), [messages]);

  // Ensure the two loading states are never shown together
  const shouldShowThinkingMessage = showThinkingMessage && !showProgressDataPart;
  const handleReaction = useCallback((messageUuid: string, reaction: MessageReaction | null) => {
    setReactions(prev => ({
      ...prev,
      [messageUuid]: reaction
    }));
  }, []);
  return <div className="relative flex size-full min-w-0 flex-col">
      <div ref={messagesContainerRef} className={`relative flex size-full min-w-0 flex-col gap-6 overflow-y-auto px-4 pt-4 ${className || ''}`}>
        {messages.length === 0 && !isLoading && <Greeting message={welcomeMessage} />}

        {messages.map(message => <MessageRenderer key={message.id} message={{
        ...message,
        metadata: {
          ...message.metadata,
          reaction: reactions[message.id] ?? message.metadata?.reaction,
          files: message.metadata?.files ?? []
        }
      }} isLast={message.id === messages.at(-1)?.id} isStreaming={status === 'streaming'} onReaction={reaction_0 => handleReaction(message.id, reaction_0)} sourceFallbackMap={priorSourcesByMessageId.get(message.id)} toolRenderers={toolRenderers} dataPartRenderers={dataPartRenderers} enableSourcesRendering={enableSourcesRendering} />)}

        <ThinkingMessage show={shouldShowThinkingMessage} />
        {showProgressDataPart && <ProgressDataPart />}

        {/* <InlineError onRetry={onRetry} /> */}

        {isLoading && <div className="absolute inset-0 z-10 flex items-center justify-center">
            <div className="bg-background/90 border-border/20 rounded-lg border p-4 shadow-lg backdrop-blur-sm">
              <div className="flex items-center gap-3">
                <Loader2 className="size-4 animate-spin" />
                <span className="text-muted-foreground text-sm">Loading messages...</span>
              </div>
            </div>
          </div>}

        <motion.div ref={messagesEndRef} className="min-h-[24px] min-w-[24px] shrink-0" onViewportLeave={onViewportLeave} onViewportEnter={onViewportEnter} />
      </div>

      <button aria-label="Scroll to bottom" className={`bg-background hover:bg-muted absolute bottom-4 left-1/2 z-10 -translate-x-1/2 rounded-full border p-2 shadow-lg transition-all ${isAtBottom ? 'pointer-events-none scale-0 opacity-0' : 'pointer-events-auto scale-100 opacity-100'}`} onClick={() => scrollToBottom('smooth')} type="button">
        <ArrowDownIcon className="size-4" />
      </button>
    </div>;
}
const TOOLS_WITH_NO_RESULT_TEXT = ['getPolicyByFilters', 'getInvoiceByFilters', 'summarizeEntities', 'getAccountByUuid', 'processFiles'];
const PENDING_TOOL_INVOCATION_STATES = ['input-streaming', 'input-available'];
function getShowThinkingMessage(status: string, messages: UIMessageWithMetadata[]) {
  if (status === 'submitted') {
    return true;
  }
  const isStreaming = status === 'streaming';
  const hasMessages = isNotEmpty(messages);
  const lastMessage = hasMessages ? messages[messages.length - 1] : undefined;
  const lastMessageHasNoParts = isEmpty(lastMessage?.parts);
  const lastMessageLastPartIsStepStart = isNotEmpty(lastMessage?.parts) && lastMessage.parts[lastMessage.parts.length - 1].type === 'step-start';
  const lastMessageLastPartIsToolInvocationResultWithNoResultText = isNotEmpty(lastMessage?.parts) && lastMessage.parts[lastMessage.parts.length - 1].type === 'tool-invocation' && TOOLS_WITH_NO_RESULT_TEXT.includes(get(lastMessage.parts[lastMessage.parts.length - 1], 'toolInvocation.toolName', ''));
  const lastMessageLastPart = lastMessage?.parts[lastMessage.parts.length - 1];
  const lastMessageIsToolStreaming = isNotEmpty(lastMessageLastPart) && isStreamUIPart(lastMessageLastPart) && PENDING_TOOL_INVOCATION_STATES.includes(lastMessageLastPart.state);
  const streamingAndLastMessageLastPartIsStepStart = isStreaming && lastMessageLastPartIsStepStart;
  const streamingAndLastMessageHasNoParts = isStreaming && lastMessageHasNoParts;
  const streamingAndLastMessageLastPartIsToolInvocationResultWithNoResultText = isStreaming && lastMessageLastPartIsToolInvocationResultWithNoResultText;
  const isReasoningLastPart = isNotEmpty(lastMessageLastPart) && lastMessageLastPart.type === 'reasoning';
  return streamingAndLastMessageLastPartIsStepStart || streamingAndLastMessageHasNoParts || streamingAndLastMessageLastPartIsToolInvocationResultWithNoResultText || lastMessageIsToolStreaming || isReasoningLastPart;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isStreamUIPart(part: UIMessagePart<any, any>): part is ToolUIPart {
  return part.type.startsWith('tool-');
}